// src/components/ServerDataExtraction/ExtractionResults.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { parseMinecraftFormatting } from '../../utils/minecraftFormatting';
import { useLanguage } from '../../contexts/LanguageContext';

const ExtractionResults = ({
  serverData,
  extractionStatus = 'idle',
  isLoading = false,
  isDisabled = false,
  progress = null,
  options = {},
  outputFormat = 'json',
  onFormatChange,
  onExtract,
  onCopy,
  onDownload,
  targetHost
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [activeTab, setActiveTab] = useState('overview');
  const [playerFilter, setPlayerFilter] = useState('');
  const [cveFilter, setCveFilter] = useState('all');
  const [modFilter, setModFilter] = useState('');
  const [faviconError, setFaviconError] = useState(false);

  // Check if real extracted data exists
  const hasExtractedData = Boolean(
    serverData &&
    serverData.success !== false &&
    (
      serverData.ip ||
      serverData.hostname ||
      serverData.version ||
      serverData.motd ||
      serverData.onlinePlayers !== undefined ||
      (Array.isArray(serverData.players) && serverData.players.length > 0) ||
      (Array.isArray(serverData.plugins) && serverData.plugins.length > 0) ||
      (Array.isArray(serverData.cveList) && serverData.cveList.length > 0) ||
      serverData.geo ||
      serverData.protection ||
      (Array.isArray(serverData.ports) && serverData.ports.length > 0) ||
      (Array.isArray(serverData.subdomains) && serverData.subdomains.length > 0) ||
      (Array.isArray(serverData.mods) && serverData.mods.length > 0)
    )
  );

  const isCurrentlyExtracting = isLoading || extractionStatus === 'extracting';
  const isError = extractionStatus === 'error' && !hasExtractedData;
  const isIdle = !hasExtractedData && !isCurrentlyExtracting && !isError;

  // Reset to overview tab if target changes or resets
  useEffect(() => {
    if (isIdle || isError) {
      setActiveTab('overview');
    }
  }, [isIdle, isError]);

  const displayData = hasExtractedData ? (serverData || {}) : {};

  // Extract raw & clean MOTD
  const motdRaw = typeof displayData.motd === 'object'
    ? (displayData.motd?.raw || displayData.motd?.clean || '')
    : (typeof displayData.motd === 'string' ? displayData.motd : '');

  const motdClean = typeof displayData.motd === 'object'
    ? (displayData.motd?.clean || displayData.motd?.raw || '')
    : (typeof displayData.motd === 'string' ? displayData.motd : '');

  // Online players count & limit
  const onlineCount = displayData.onlinePlayers !== undefined
    ? displayData.onlinePlayers
    : (displayData.players?.online !== undefined ? displayData.players.online : (displayData.online ? 1 : 0));

  const maxSlots = displayData.maxPlayers !== undefined
    ? displayData.maxPlayers
    : (displayData.players?.max !== undefined ? displayData.players.max : 1000);

  // Players list
  const playersList = useMemo(() => {
    if (Array.isArray(displayData.players)) {
      return displayData.players.map(p => typeof p === 'string' ? { name: p } : p);
    }
    if (Array.isArray(displayData.players?.list)) {
      return displayData.players.list.map(p => typeof p === 'string' ? { name: p } : p);
    }
    if (Array.isArray(displayData.players?.sample)) {
      return displayData.players.sample.map(p => typeof p === 'string' ? { name: p } : p);
    }
    return [];
  }, [displayData.players]);

  const filteredPlayers = useMemo(() => {
    if (!playerFilter.trim()) return playersList;
    const q = playerFilter.toLowerCase().trim();
    return playersList.filter(p => (p.name || '').toLowerCase().includes(q));
  }, [playersList, playerFilter]);

  // Plugins list
  const pluginsList = useMemo(() => {
    if (Array.isArray(displayData.plugins)) {
      return displayData.plugins.map(pl => {
        if (typeof pl === 'string') return { name: pl, version: '' };
        return { name: pl.name || 'Plugin', version: pl.version || '' };
      });
    }
    return [];
  }, [displayData.plugins]);

  // CVE list
  const cveList = useMemo(() => {
    if (Array.isArray(displayData.cveList)) return displayData.cveList;
    if (Array.isArray(displayData.vulnerabilities)) return displayData.vulnerabilities;
    return [];
  }, [displayData]);

  const filteredCves = useMemo(() => {
    if (cveFilter === 'all') return cveList;
    return cveList.filter(c => (c.severity || '').toLowerCase() === cveFilter.toLowerCase());
  }, [cveList, cveFilter]);

  const criticalCveCount = useMemo(() => {
    return cveList.filter(c => {
      const s = (c.severity || '').toUpperCase();
      return s === 'CRITICAL' || s === 'HIGH';
    }).length;
  }, [cveList]);

  // Geo & Datacenter
  const geoData = displayData.geo || displayData.datacenter || null;

  // Anti-DDoS & WAF Protection
  const wafData = displayData.protection || displayData.waf || null;

  // Ports
  const portsList = useMemo(() => {
    if (Array.isArray(displayData.ports)) return displayData.ports;
    if (Array.isArray(displayData.servicePorts)) return displayData.servicePorts;
    return [];
  }, [displayData]);

  const openPortsCount = useMemo(() => {
    return portsList.filter(p => (p.status || '').toUpperCase() === 'OPEN').length;
  }, [portsList]);

  // Subdomains
  const subdomainsList = useMemo(() => {
    if (Array.isArray(displayData.subdomains)) return displayData.subdomains;
    return [];
  }, [displayData]);

  // DNS records
  const dnsList = useMemo(() => {
    if (Array.isArray(displayData.dnsHistory)) return displayData.dnsHistory;
    if (Array.isArray(displayData.dnsRecords)) return displayData.dnsRecords;
    return [];
  }, [displayData]);

  // Mods
  const modsList = useMemo(() => {
    if (Array.isArray(displayData.mods)) {
      return displayData.mods.map(m => typeof m === 'string' ? { name: m } : m);
    }
    return [];
  }, [displayData]);

  const filteredMods = useMemo(() => {
    if (!modFilter.trim()) return modsList;
    const q = modFilter.toLowerCase().trim();
    return modsList.filter(m => (m.name || '').toLowerCase().includes(q));
  }, [modsList, modFilter]);

  // Ping
  const pingDisplay = displayData.ping !== undefined && displayData.ping !== null
    ? (typeof displayData.ping === 'number' ? `${displayData.ping} ms` : String(displayData.ping))
    : '24 ms';

  // Version
  const versionDisplay = displayData.version?.name || displayData.version || '1.20.4';
  const protocolDisplay = displayData.protocol ? `Protocol #${displayData.protocol}` : 'Handshake OK';

  // Favicon
  useEffect(() => {
    setFaviconError(false);
  }, [displayData.icon, displayData.favicon]);

  const rawFavicon = displayData.icon || displayData.favicon || null;
  const faviconSrc = !faviconError && rawFavicon ? rawFavicon : null;

  // Download Favicon as PNG
  const handleDownloadFavicon = useCallback(() => {
    if (!faviconSrc) return;
    const a = document.createElement('a');
    a.href = faviconSrc;
    const hostPart = (displayData.hostname || displayData.ip || targetHost || 'server').replace(/[:.]/g, '_');
    a.download = `${hostPart}_favicon.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [faviconSrc, displayData, targetHost]);

  // Format Code for Code Viewer and Export (respects options)
  const formattedCode = useMemo(() => {
    const host = displayData.hostname || displayData.ip || targetHost || 'server';
    const port = displayData.port || 25565;

    if (outputFormat === 'json') {
      const dumpObj = {
        auditTarget: {
          host,
          port: parseInt(port, 10),
          resolvedIp: displayData.ip || host,
          pingMs: displayData.ping || 0,
          extractedAt: new Date().toISOString()
        }
      };

      if (options.serverInfo !== false) {
        dumpObj.serverDetails = {
          version: versionDisplay,
          protocol: displayData.protocol || null,
          software: displayData.software || displayData.gamemode || 'Minecraft Server',
          map: displayData.map || undefined
        };
      }

      if (options.motd !== false) {
        dumpObj.motd = {
          raw: motdRaw,
          clean: motdClean
        };
      }

      if (options.serverIcon !== false) {
        dumpObj.favicon = {
          present: !!faviconSrc,
          data: faviconSrc || null
        };
      }

      if (options.playerList !== false) {
        dumpObj.players = {
          online: onlineCount,
          max: maxSlots,
          sample: playersList.map(p => p.name)
        };
      }

      if (options.pluginList !== false) {
        dumpObj.pluginsDiscovered = pluginsList;
      }

      if (options.dnsHistory !== false && displayData.dnsHistory) {
        dumpObj.dnsHistory = displayData.dnsHistory;
      }
      if (options.subdomainScan !== false && displayData.subdomains) {
        dumpObj.subdomains = displayData.subdomains;
      }
      if (options.wafDetection !== false && (displayData.waf || displayData.protection)) {
        dumpObj.protection = displayData.waf || displayData.protection;
      }
      if (options.portScan !== false && displayData.ports) {
        dumpObj.servicePorts = displayData.ports;
      }
      if (options.cveAudit !== false && displayData.cveList) {
        dumpObj.vulnerabilities = displayData.cveList;
      }
      if (options.modsManifest !== false && displayData.mods) {
        dumpObj.mods = displayData.mods;
      }
      if (options.geoDatacenter !== false && displayData.geo) {
        dumpObj.datacenter = displayData.geo;
      }

      return JSON.stringify(dumpObj, null, 2);
    }

    if (outputFormat === 'html') {
      let sectionsHtml = '';
      if (options.serverInfo !== false) {
        sectionsHtml += `  <div class="card">
    <h2>Server Information</h2>
    <p>Version: <b>${versionDisplay}</b> | Latency: <b>${pingDisplay}</b></p>
    <p>Software: <b>${displayData.software || displayData.gamemode || 'Standard'}</b></p>
  </div>\n`;
      }
      if (options.motd !== false) {
        sectionsHtml += `  <div class="card">
    <h2>MOTD</h2>
    <pre class="motd">${motdClean || 'N/A'}</pre>
  </div>\n`;
      }
      if (options.playerList !== false) {
        sectionsHtml += `  <div class="card">
    <h2>Players (<span class="kpi">${onlineCount}</span> / ${maxSlots})</h2>
    <ul>
${playersList.length ? playersList.map(p => `      <li>${p.name}</li>`).join('\n') : '      <li>No player sample returned</li>'}
    </ul>
  </div>\n`;
      }
      if (options.pluginList !== false) {
        sectionsHtml += `  <div class="card">
    <h2>Plugins Discovered (${pluginsList.length})</h2>
    <ul>
${pluginsList.length ? pluginsList.map(p => `      <li>${p.name}${p.version ? ` (${p.version})` : ''}</li>`).join('\n') : '      <li>No plugins detected</li>'}
    </ul>
  </div>\n`;
      }

      if (options.cveAudit !== false && cveList.length > 0) {
        sectionsHtml += `  <div class="card">
    <h2>CVE Security Audit (${cveList.length})</h2>
    <ul>
${cveList.map(c => `      <li>[${c.severity || 'INFO'}] <b>${c.cveId || ''}</b>: ${c.title || ''} (${c.affectedVersions || ''})</li>`).join('\n')}
    </ul>
  </div>\n`;
      }
      if (options.wafDetection !== false && wafData) {
        sectionsHtml += `  <div class="card">
    <h2>DDoS Protection & WAF</h2>
    <p>Status: <b>${wafData.detected ? 'PROTECTED' : 'DIRECT NODE'}</b> | Provider: <b>${wafData.provider || 'N/A'}</b></p>
    <p>${wafData.details || ''}</p>
  </div>\n`;
      }
      if (options.geoDatacenter !== false && geoData) {
        sectionsHtml += `  <div class="card">
    <h2>Datacenter & Geolocation</h2>
    <p>Location: <b>${geoData.city || ''}, ${geoData.country || ''}</b> (${geoData.countryCode || ''})</p>
    <p>ISP: <b>${geoData.isp || 'N/A'}</b> | ASN: <b>${geoData.as || 'N/A'}</b></p>
  </div>\n`;
      }
      if (options.portScan !== false && portsList.length > 0) {
        sectionsHtml += `  <div class="card">
    <h2>Service Ports Recon</h2>
    <ul>
${portsList.map(p => `      <li>Port ${p.port} (${p.service}): <b>${p.status}</b></li>`).join('\n')}
    </ul>
  </div>\n`;
      }
      if (options.subdomainScan !== false && subdomainsList.length > 0) {
        sectionsHtml += `  <div class="card">
    <h2>Discovered Subdomains (${subdomainsList.length})</h2>
    <ul>
${subdomainsList.map(s => `      <li>${s.subdomain} &rarr; ${s.ip}</li>`).join('\n')}
    </ul>
  </div>\n`;
      }
      if (options.modsManifest !== false && modsList.length > 0) {
        sectionsHtml += `  <div class="card">
    <h2>Forge / Fabric Mods (${modsList.length})</h2>
    <ul>
${modsList.map(m => `      <li>${m.name} ${m.version ? `(${m.version})` : ''}</li>`).join('\n')}
    </ul>
  </div>\n`;
      }

      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>StormBlack Dump - ${host}</title>
  <style>
    body { background: #08090c; color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 32px; max-width: 900px; margin: 0 auto; }
    h1 { font-family: monospace; color: #14b8a6; margin-bottom: 8px; }
    .subtitle { color: #9ca3af; font-size: 14px; margin-bottom: 24px; font-family: monospace; }
    .card { background: #111319; border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 18px 22px; margin-bottom: 16px; }
    h2 { font-size: 16px; color: #fff; margin-top: 0; margin-bottom: 12px; border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 8px; }
    .kpi { color: #30d158; font-weight: bold; }
    ul { font-family: monospace; font-size: 13px; color: #d1d5db; line-height: 1.6; margin: 0; padding-left: 20px; }
    pre.motd { background: #0c0e14; padding: 12px; border-radius: 8px; font-family: monospace; white-space: pre-wrap; color: #fde047; }
  </style>
</head>
<body>
  <h1>StormBlack Server Audit: ${host}:${port}</h1>
  <div class="subtitle">Extracted at: ${new Date().toISOString()}</div>
${sectionsHtml}</body>
</html>`;
    }

    if (outputFormat === 'yaml') {
      let yamlStr = `# StormBlack Server Audit Dump\n`;
      yamlStr += `target:\n  host: "${host}"\n  port: ${port}\n  ping: "${pingDisplay}"\n`;

      if (options.serverInfo !== false) {
        yamlStr += `server:\n  version: "${versionDisplay}"\n  software: "${displayData.software || displayData.gamemode || 'Standard'}"\n`;
      }
      if (options.motd !== false) {
        yamlStr += `motd:\n  clean: "${motdClean.replace(/"/g, '\\"')}"\n`;
      }
      if (options.playerList !== false) {
        yamlStr += `players:\n  online: ${onlineCount}\n  max: ${maxSlots}\n`;
        if (playersList.length > 0) {
          yamlStr += `  sample:\n${playersList.map(p => `    - "${p.name}"`).join('\n')}\n`;
        }
      }
      if (options.pluginList !== false) {
        yamlStr += `plugins_count: ${pluginsList.length}\n`;
        if (pluginsList.length > 0) {
          yamlStr += `plugins:\n${pluginsList.map(p => `  - name: "${p.name}"\n    version: "${p.version || 'unknown'}"`).join('\n')}\n`;
        }
      }
      if (options.cveAudit !== false && cveList.length > 0) {
        yamlStr += `vulnerabilities:\n${cveList.map(c => `  - id: "${c.cveId}"\n    severity: "${c.severity}"\n    title: "${c.title}"\n    mitigation: "${c.mitigation || ''}"`).join('\n')}\n`;
      }
      if (options.wafDetection !== false && wafData) {
        yamlStr += `protection:\n  detected: ${wafData.detected}\n  provider: "${wafData.provider || 'none'}"\n  details: "${wafData.details || ''}"\n`;
      }
      if (options.geoDatacenter !== false && geoData) {
        yamlStr += `datacenter:\n  country: "${geoData.country}"\n  city: "${geoData.city}"\n  isp: "${geoData.isp}"\n  asn: "${geoData.as}"\n`;
      }
      if (options.portScan !== false && portsList.length > 0) {
        yamlStr += `ports:\n${portsList.map(p => `  - port: ${p.port}\n    service: "${p.service}"\n    status: "${p.status}"`).join('\n')}\n`;
      }
      if (options.subdomainScan !== false && subdomainsList.length > 0) {
        yamlStr += `subdomains:\n${subdomainsList.map(s => `  - host: "${s.subdomain}"\n    ip: "${s.ip}"`).join('\n')}\n`;
      }
      if (options.modsManifest !== false && modsList.length > 0) {
        yamlStr += `mods:\n${modsList.map(m => `  - name: "${m.name}"\n    version: "${m.version || ''}"`).join('\n')}\n`;
      }
      return yamlStr;
    }

    // Default TXT
    let txtStr = `=== STORMBLACK SERVER DATA EXTRACTION ===\n`;
    txtStr += `Цель: ${host}:${port}\n`;
    txtStr += `Пинг: ${pingDisplay}\n`;
    txtStr += `Дата выкачки: ${new Date().toLocaleString()}\n\n`;

    if (options.serverInfo !== false) {
      txtStr += `[ ИНФОРМАЦИЯ О СЕРВЕРЕ ]\n`;
      txtStr += `Версия: ${versionDisplay} (${protocolDisplay})\n`;
      txtStr += `ПО / Ядро: ${displayData.software || displayData.gamemode || 'N/A'}\n\n`;
    }

    if (options.motd !== false) {
      txtStr += `[ MOTD ]\n`;
      txtStr += `${motdClean || 'N/A'}\n\n`;
    }

    if (options.playerList !== false) {
      txtStr += `[ ОНЛАЙН И ИГРОКИ ]\n`;
      txtStr += `Онлайн: ${onlineCount} / ${maxSlots}\n`;
      if (playersList.length > 0) {
        txtStr += `Выборка игроков (${playersList.length}):\n`;
        txtStr += playersList.map(p => `- ${p.name}`).join('\n') + '\n';
      }
      txtStr += '\n';
    }

    if (options.pluginList !== false) {
      txtStr += `[ СПИСОК ПЛАГИНОВ (${pluginsList.length}) ]\n`;
      if (pluginsList.length > 0) {
        txtStr += pluginsList.map(p => `- ${p.name}${p.version ? ` [${p.version}]` : ''}`).join('\n') + '\n';
      } else {
        txtStr += `Плагины не обнаружены или скрыты сервером.\n`;
      }
      txtStr += '\n';
    }

    if (options.cveAudit !== false && cveList.length > 0) {
      txtStr += `[ АУДИТ БЕЗОПАСНОСТИ И УЯЗВИМОСТЕЙ CVE (${cveList.length}) ]\n`;
      txtStr += cveList.map(c => `[${c.severity}] ${c.cveId}: ${c.title}\n  Описание: ${c.description}\n  Устранение: ${c.mitigation || 'N/A'}`).join('\n\n') + '\n\n';
    }
    if (options.wafDetection !== false && wafData) {
      txtStr += `[ ЗАЩИТА И СЕТЕВОЙ ЭКРАН ]\n`;
      txtStr += `Статус: ${wafData.detected ? 'АКТИВНА' : 'ПРЯМОЕ ПОДКЛЮЧЕНИЕ'}\n`;
      txtStr += `Провайдер: ${wafData.provider || 'N/A'}\n`;
      txtStr += `Детали: ${wafData.details || 'N/A'}\n\n`;
    }
    if (options.geoDatacenter !== false && geoData) {
      txtStr += `[ ГЕОЛОКАЦИЯ И ДАТА-ЦЕНТР ]\n`;
      txtStr += `Локация: ${geoData.city}, ${geoData.country} (${geoData.countryCode})\n`;
      txtStr += `Провайдер / ASN: ${geoData.isp} / ${geoData.as}\n\n`;
    }
    if (options.portScan !== false && portsList.length > 0) {
      txtStr += `[ АУДИТ ПОРТОВ (${portsList.length}) ]\n`;
      txtStr += portsList.map(p => `- Порт ${p.port} (${p.service}): ${p.status}`).join('\n') + '\n\n';
    }
    if (options.subdomainScan !== false && subdomainsList.length > 0) {
      txtStr += `[ НАЙДЕННЫЕ ПОДДОМЕНЫ (${subdomainsList.length}) ]\n`;
      txtStr += subdomainsList.map(s => `- ${s.subdomain} -> ${s.ip}`).join('\n') + '\n\n';
    }
    if (options.modsManifest !== false && modsList.length > 0) {
      txtStr += `[ МОДЫ FORGE / FABRIC (${modsList.length}) ]\n`;
      txtStr += modsList.map(m => `- ${m.name}${m.version ? ` [${m.version}]` : ''}`).join('\n') + '\n\n';
    }

    return txtStr;
  }, [displayData, outputFormat, options, targetHost, versionDisplay, protocolDisplay, pingDisplay, motdRaw, motdClean, faviconSrc, onlineCount, maxSlots, playersList, pluginsList, cveList, geoData, wafData, portsList, subdomainsList, modsList, dnsList]);

  // Code size & stats
  const codeBytes = useMemo(() => {
    return new Blob([formattedCode]).size;
  }, [formattedCode]);

  const codeSizeFormatted = useMemo(() => {
    if (codeBytes < 1024) return `${codeBytes} B`;
    return `${(codeBytes / 1024).toFixed(1)} KB`;
  }, [codeBytes]);

  const codeLinesCount = useMemo(() => {
    return formattedCode ? formattedCode.split('\n').length : 0;
  }, [formattedCode]);

  // Syntax highlighting for JSON
  const renderHighlightedJson = useCallback((jsonStr) => {
    const escaped = jsonStr
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'token-num';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'token-key';
          } else {
            cls = 'token-str';
          }
        } else if (/true|false/.test(match)) {
          cls = 'token-bool';
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }, []);

  // Syntax highlighting for YAML
  const renderHighlightedYaml = useCallback((yamlStr) => {
    const escaped = yamlStr
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped
      .replace(/^(\s*#.*)$/gm, '<span class="token-comment">$1</span>')
      .replace(/^(\s*)([a-zA-Z0-9_-]+)(:)/gm, '$1<span class="token-key">$2</span>$3')
      .replace(/:\s*(".*?"|'.*?')$/gm, ': <span class="token-str">$1</span>')
      .replace(/:\s*(true|false)\b/gm, ': <span class="token-bool">$1</span>')
      .replace(/:\s*(\d+)\b/gm, ': <span class="token-num">$1</span>');
  }, []);

  // Syntax highlighting for HTML
  const renderHighlightedHtml = useCallback((htmlStr) => {
    const escaped = htmlStr
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return escaped.replace(/(&lt;\/?)([a-zA-Z0-9]+)(.*?)(&gt;)/g, (match, p1, p2, p3, p4) => {
      const highlightedAttrs = p3.replace(/([a-zA-Z-]+)=(".*?"|'.*?')/g, '<span class="token-key">$1</span>=<span class="token-str">$2</span>');
      return `${p1}<span class="token-tag">${p2}</span>${highlightedAttrs}${p4}`;
    });
  }, []);

  // Progress status derivation
  const progressPercent = isCurrentlyExtracting ? (progress?.percent || 45) : 100;
  const progressMessage = isCurrentlyExtracting
    ? (progress?.message || (isEn ? 'Extracting server data...' : 'Выкачивание данных...'))
    : (isError
      ? (progress?.message || (isEn ? 'Extraction failed' : 'Ошибка извлечения данных'))
      : (isEn ? 'Done • All data extracted (100%)' : 'Готово • Извлечены все данные (100%)'));

  return (
    <div className={`glass-card results-card ${isDisabled ? 'opacity-50' : ''}`}>

      {/* 1. IDLE STATE: When data has not been extracted yet */}
      {isIdle && (
        <div className="flex flex-col h-full flex-1 items-center justify-center">
          <div className="extraction-idle-box">
            <div className="idle-icon-wrap">
              <div className="idle-icon-box">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                  <path d="M12 12v9" />
                  <path d="m8 17 4 4 4-4" />
                </svg>
              </div>
              <div className="idle-icon-pulse" />
            </div>

            <h3 className="idle-heading">
              {isEn ? 'Data will appear here' : 'Здесь появятся данные'}
            </h3>
            <p className="idle-subtext">
              {isEn
                ? 'Select extraction parameters on the left and launch the process to retrieve detailed server intelligence.'
                : 'Выберите нужные параметры слева и запустите извлечение данных, чтобы получить подробную информацию о сервере.'}
            </p>
          </div>
        </div>
      )}

      {/* 2. EXTRACTING STATE */}
      {isCurrentlyExtracting && (
        <div className="flex flex-col h-full flex-1">
          <div className="extraction-progress-box">
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="progress-status-line">
              <span className="progress-status-text">
                <span
                  className="beacon-pulse"
                  style={{
                    background: '#eab308',
                    boxShadow: '0 0 10px #eab308'
                  }}
                />
                <span>{progressMessage}</span>
              </span>
              <span className="progress-percent text-amber-400">
                {progressPercent}%
              </span>
            </div>
          </div>

          <div className="extraction-scanning-box">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-2xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                <span className="w-8 h-8 border-2 border-teal-500/20 border-t-teal-400 rounded-full animate-spin" />
              </div>
            </div>
            <h4 className="text-xl font-bold text-white mb-2 font-display uppercase tracking-wider">
              {isEn ? 'Extracting Server Data...' : 'Выполняется извлечение данных...'}
            </h4>
            <p className="text-xs font-mono text-white/50 max-w-md">
              {targetHost ? `Target: ${targetHost} • Establishing socket pipeline & querying metadata` : (isEn ? 'Handshake in progress...' : 'Подключение к сокету...')}
            </p>
          </div>
        </div>
      )}

      {/* 3. ERROR STATE */}
      {isError && (
        <div className="flex flex-col h-full flex-1">
          <div className="extraction-progress-box">
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill !bg-red-500"
                style={{ width: '100%' }}
              />
            </div>
            <div className="progress-status-line">
              <span className="progress-status-text">
                <span
                  className="beacon-pulse"
                  style={{
                    background: '#ef4444',
                    boxShadow: '0 0 10px #ef4444'
                  }}
                />
                <span>{progressMessage}</span>
              </span>
              <span className="progress-percent text-red-400">
                ERROR
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center py-16 px-6 my-auto">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4 text-red-400">
              <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-white mb-2 font-display uppercase tracking-wider">
              {isEn ? 'Extraction Failed' : 'Ошибка извлечения данных'}
            </h4>
            <p className="text-xs text-red-300/80 max-w-md mb-6 font-mono">
              {progressMessage || (isEn ? 'Server unreachable or refused TCP query handshake.' : 'Сервер недоступен или отклонил опрос.')}
            </p>
            <button
              type="button"
              onClick={onExtract}
              className="tool-icon-btn primary-download"
            >
              {isEn ? 'Retry Extraction' : 'Повторить попытку'}
            </button>
          </div>
        </div>
      )}

      {/* 4. COMPLETED RESULTS TABS */}
      {hasExtractedData && !isCurrentlyExtracting && (
        <>
          {/* Top Bar: Tabs & Action Buttons */}
          <div className="results-top-bar">
            <div className="results-tabs-wrap">
              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <svg viewBox="0 0 24 24">
                  <rect width="7" height="9" x="3" y="3" rx="1" />
                  <rect width="7" height="5" x="14" y="3" rx="1" />
                  <rect width="7" height="9" x="14" y="12" rx="1" />
                  <rect width="7" height="5" x="3" y="16" rx="1" />
                </svg>
                <span>{isEn ? 'Overview' : 'Обзор'}</span>
              </button>

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'motd' ? 'active' : ''}`}
                onClick={() => setActiveTab('motd')}
              >
                <svg viewBox="0 0 24 24">
                  <rect width="18" height="18" x="3" y="3" rx="2" />
                  <path d="m9 8 6 4-6 4Z" />
                </svg>
                <span>{isEn ? 'MOTD & Icon' : 'MOTD & Иконка'}</span>
              </button>

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'players' ? 'active' : ''}`}
                onClick={() => setActiveTab('players')}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>{isEn ? 'Players' : 'Игроки'} {playersList.length > 0 && `(${playersList.length})`}</span>
              </button>

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'plugins' ? 'active' : ''}`}
                onClick={() => setActiveTab('plugins')}
              >
                <svg viewBox="0 0 24 24">
                  <path d="m21 16-4 4-4-4" />
                  <path d="M17 20V4" />
                  <path d="m3 8 4-4 4 4" />
                  <path d="M7 4v16" />
                </svg>
                <span>{isEn ? 'Plugins' : 'Плагины'} {pluginsList.length > 0 && `(${pluginsList.length})`}</span>
              </button>

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'cve' ? 'active' : ''}`}
                onClick={() => setActiveTab('cve')}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{isEn ? 'CVE Audit' : 'CVE Аудит'} {cveList.length > 0 && `(${cveList.length})`}</span>
              </button>

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'network' ? 'active' : ''}`}
                onClick={() => setActiveTab('network')}
              >
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <span>{isEn ? 'Network & OSINT' : 'Сеть & OSINT'}</span>
              </button>

              {(modsList.length > 0 || options.modsManifest) && (
                <button
                  type="button"
                  className={`res-tab-btn ${activeTab === 'mods' ? 'active' : ''}`}
                  onClick={() => setActiveTab('mods')}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                  <span>{isEn ? 'Mods' : 'Моды'} {modsList.length > 0 && `(${modsList.length})`}</span>
                </button>
              )}

              <button
                type="button"
                className={`res-tab-btn ${activeTab === 'code' ? 'active' : ''}`}
                onClick={() => setActiveTab('code')}
              >
                <svg viewBox="0 0 24 24">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
                <span>{isEn ? 'Code' : 'Код'}</span>
              </button>
            </div>

            {/* Quick Actions in Top Bar */}
            <div className="results-actions-group">
              <button
                type="button"
                className="tool-icon-btn"
                onClick={() => onCopy && onCopy(formattedCode)}
                title={isEn ? 'Copy data to clipboard' : 'Скопировать данные в буфер'}
              >
                <svg viewBox="0 0 24 24">
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
                <span>{isEn ? 'Copy' : 'Копировать'}</span>
              </button>
            </div>
          </div>

          {/* Progress / Status Header */}
          <div className="extraction-progress-box">
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: '100%' }}
              />
            </div>
            <div className="progress-status-line">
              <span className="progress-status-text">
                <span
                  className="beacon-pulse"
                  style={{
                    background: '#30d158',
                    boxShadow: '0 0 10px #30d158'
                  }}
                />
                <span>{progressMessage}</span>
              </span>
              <span className="progress-percent text-teal-400">
                {codeSizeFormatted}
              </span>
            </div>
          </div>

          {/* TAB 1: OVERVIEW */}
          <div className={`tab-pane ${activeTab === 'overview' ? 'active' : ''}`}>
            <div className="overview-kpi-grid">
              <div className="kpi-box">
                <span className="kpi-title">{isEn ? 'Online Players' : 'Игроки онлайн'}</span>
                <span className="kpi-value">{typeof onlineCount === 'number' ? onlineCount.toLocaleString() : onlineCount}</span>
                <span className="kpi-note">{isEn ? `of ${typeof maxSlots === 'number' ? maxSlots.toLocaleString() : maxSlots} slots` : `из ${typeof maxSlots === 'number' ? maxSlots.toLocaleString() : maxSlots} слотов`}</span>
              </div>

              <div className="kpi-box">
                <span className="kpi-title">{isEn ? 'Ping / Latency' : 'Пинг / Задержка'}</span>
                <span className="kpi-value">{pingDisplay}</span>
                <span className="kpi-note">{isEn ? 'Stable handshake' : 'Стабильный handshake'}</span>
              </div>

              <div className="kpi-box">
                <span className="kpi-title">{isEn ? 'Core Version' : 'Версия ядра'}</span>
                <span className="kpi-value" style={{ fontSize: '1.05rem' }}>{versionDisplay}</span>
                <span className="kpi-note">{protocolDisplay}</span>
              </div>

              <div className="kpi-box">
                <span className="kpi-title">{isEn ? 'Plugins Discovered' : 'Плагинов найдено'}</span>
                <span className="kpi-value">{pluginsList.length}</span>
                <span className="kpi-note">{isEn ? 'Via Tab-completion' : 'Через Tab-complete'}</span>
              </div>
            </div>

            {/* Overview Security & Network Summary Pills */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {/* Datacenter pill */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-2.5 text-left flex items-center gap-2.5">
                <span className="text-base shrink-0">🌍</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase text-white/40 font-bold">{isEn ? 'Datacenter' : 'Дата-центр'}</div>
                  <div className="text-xs font-semibold text-white truncate">
                    {geoData ? `${geoData.countryCode || ''} ${geoData.city || geoData.country || 'Detected'}` : (isEn ? 'Direct Host' : 'Прямой узел')}
                  </div>
                </div>
              </div>

              {/* Anti-DDoS pill */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-2.5 text-left flex items-center gap-2.5">
                <span className="text-base shrink-0">🛡️</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase text-white/40 font-bold">{isEn ? 'Anti-DDoS' : 'Защита'}</div>
                  <div className={`text-xs font-semibold truncate ${wafData?.detected ? 'text-emerald-400' : 'text-white/80'}`}>
                    {wafData?.provider || (isEn ? 'Direct Node' : 'Прямой узел')}
                  </div>
                </div>
              </div>

              {/* CVE Status pill */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-2.5 text-left flex items-center gap-2.5">
                <span className="text-base shrink-0">{criticalCveCount > 0 ? '⚠️' : '✅'}</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase text-white/40 font-bold">{isEn ? 'CVE Status' : 'CVE Статус'}</div>
                  <div className={`text-xs font-semibold truncate ${criticalCveCount > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                    {criticalCveCount > 0 ? (isEn ? `${criticalCveCount} Critical` : `${criticalCveCount} критич.`) : (isEn ? 'Baseline Clean' : 'Безопасно')}
                  </div>
                </div>
              </div>

              {/* Ports pill */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-2.5 text-left flex items-center gap-2.5">
                <span className="text-base shrink-0">🔌</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase text-white/40 font-bold">{isEn ? 'Open Ports' : 'Порты'}</div>
                  <div className="text-xs font-semibold text-teal-400 truncate">
                    {portsList.length > 0 ? (isEn ? `${openPortsCount} Open` : `${openPortsCount} открыто`) : (isEn ? '25565 Open' : '25565 Открыт')}
                  </div>
                </div>
              </div>
            </div>

            {/* MOTD Preview Banner */}
            <div className="overview-motd-render">
              <div className="overview-favicon-large">
                {faviconSrc ? (
                  <img src={faviconSrc} alt="Server Favicon" />
                ) : (
                  <svg viewBox="0 0 64 64" width="64" height="64">
                    <rect width="64" height="64" fill="#111118" />
                    <polygon points="32,6 10,18 32,30 54,18" fill="#eab308" />
                    <polygon points="10,24 32,36 32,58 10,46" fill="#ca8a04" />
                    <polygon points="54,24 54,46 32,58 32,36" fill="#a16207" />
                  </svg>
                )}
              </div>
              <div className="overview-motd-body">
                <div className="mc-motd-line select-text">
                  {motdRaw ? parseMinecraftFormatting(motdRaw) : (
                    <span className="text-white/60">
                      {motdClean || (isEn ? 'No MOTD detected for this server' : 'MOTD не обнаружен для этого сервера')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Infrastructure Details */}
            <div className="bg-white/[0.025] border border-white/10 rounded-2xl p-4 text-left">
              <div className="flex justify-between items-center mb-2">
                <span className="font-mono text-xs text-white/40 uppercase font-bold">
                  {isEn ? 'Hosting Infrastructure' : 'Инфраструктура хостинга'}
                </span>
                <span className="font-mono text-xs text-white font-semibold">
                  {displayData.software || displayData.gamemode || 'Paper / Bukkit'}
                </span>
              </div>
              <div className="font-mono text-xs text-white/65 leading-relaxed select-text">
                &bull; {isEn ? 'Resolved IP:' : 'IP резолв:'} <span className="text-white font-medium">{displayData.ip || displayData.hostname || targetHost || '127.0.0.1'}</span><br />
                &bull; {isEn ? 'Port / Handshake:' : 'Порт / Handshake:'} <span className="text-white font-medium">{displayData.port || 25565}</span><br />
                &bull; {isEn ? 'Network status: Handshake accepted, packet payload verified.' : 'Сетевой статус: Handshake принят, шифрование пакетов проверено.'}
              </div>
            </div>
          </div>

          {/* TAB 2: MOTD & ICON */}
          <div className={`tab-pane ${activeTab === 'motd' ? 'active' : ''}`}>
            <div className="motd-detail-grid text-left">
              <div className="icon-inspector-card">
                <div className="favicon-stage">
                  {faviconSrc ? (
                    <img
                      src={faviconSrc}
                      alt="Server Favicon"
                      onError={() => setFaviconError(true)}
                    />
                  ) : (
                    <svg viewBox="0 0 64 64" width="48" height="48">
                      <rect width="64" height="64" fill="#111118" />
                      <polygon points="32,6 10,18 32,30 54,18" fill="#eab308" />
                      <polygon points="10,24 32,36 32,58 10,46" fill="#ca8a04" />
                      <polygon points="54,24 54,46 32,58 32,36" fill="#a16207" />
                    </svg>
                  )}
                </div>
                <div className="icon-inspector-info">
                  <h4 className="text-sm font-bold text-white leading-tight">Server Favicon</h4>
                  <p className="text-xs text-white/40 font-mono mt-0.5">64 &times; 64 px &bull; PNG</p>
                </div>
                {faviconSrc && (
                  <button
                    type="button"
                    className="tool-icon-btn icon-download-btn"
                    onClick={handleDownloadFavicon}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>{isEn ? 'Save Icon' : 'Скачать PNG'}</span>
                  </button>
                )}
              </div>

              <div className="motd-renders-column">
                <div className="motd-code-box">
                  <div className="motd-code-title">
                    <span>{isEn ? 'Formatted Render (Minecraft)' : 'Форматированный MOTD'}</span>
                  </div>
                  <div className="motd-live-frame select-text">
                    {motdRaw ? parseMinecraftFormatting(motdRaw) : (motdClean || 'N/A')}
                  </div>
                </div>

                <div className="motd-code-box">
                  <div className="motd-code-title">
                    <span>{isEn ? 'Clean Text (No Color Codes)' : 'Чистый текст (без кодов)'}</span>
                    <button
                      type="button"
                      className="motd-copy-link"
                      onClick={() => onCopy && onCopy(motdClean)}
                    >
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                      <span>{isEn ? 'Copy' : 'Копировать'}</span>
                    </button>
                  </div>
                  <pre className="motd-raw-text select-text">{motdClean || 'N/A'}</pre>
                </div>

                {motdRaw && (
                  <div className="motd-code-box">
                    <div className="motd-code-title">
                      <span>{isEn ? 'Raw MOTD String' : 'Сырая строка MOTD'}</span>
                      <button
                        type="button"
                        className="motd-copy-link"
                        onClick={() => onCopy && onCopy(motdRaw)}
                      >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                        <span>{isEn ? 'Copy' : 'Копировать'}</span>
                      </button>
                    </div>
                    <pre className="motd-raw-text select-text">{motdRaw}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* TAB 3: PLAYERS */}
          <div className={`tab-pane ${activeTab === 'players' ? 'active' : ''}`}>
            <div className="players-search-line">
              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full bg-[#0c0e14] border border-white/10 rounded-xl px-4 py-2 text-xs font-mono text-white placeholder-white/40 focus:outline-none focus:border-teal-500/50"
                  placeholder={isEn ? 'Filter player nicknames...' : 'Фильтровать ники игроков...'}
                  value={playerFilter}
                  onChange={(e) => setPlayerFilter(e.target.value)}
                />
              </div>
              <span className="font-mono text-xs text-white/50 whitespace-nowrap">
                {isEn ? 'Showing: ' : 'Показано: '}<b>{filteredPlayers.length}</b> / {playersList.length}
              </span>
            </div>

            <div className="players-grid max-h-[460px] overflow-y-auto">
              {filteredPlayers.length === 0 ? (
                <div className="col-span-full text-center py-10 font-mono text-xs text-white/40">
                  {isEn ? 'No player nicknames discovered in server sample' : 'Ники игроков не обнаружены в сэмпле сервера'}
                </div>
              ) : (
                filteredPlayers.map((player, idx) => (
                  <div key={player.name || idx} className="player-card-badge">
                    <img
                      className="player-head-avatar"
                      src={`https://mc-heads.net/avatar/${player.name}/26`}
                      alt={player.name}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"><rect width="26" height="26" fill="%23333"/></svg>';
                      }}
                    />
                    <span className="player-nick">{player.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* TAB 4: PLUGINS */}
          <div className={`tab-pane ${activeTab === 'plugins' ? 'active' : ''}`}>
            {pluginsList.length === 0 ? (
              <div className="text-center py-12 font-mono text-xs text-white/40">
                {isEn ? 'No plugins detected (server may block query or tab-completion)' : 'Плагины не обнаружены (сервер может блокировать запросы tab-complete)'}
              </div>
            ) : (
              <div className="plugins-chips-grid max-h-[460px] overflow-y-auto">
                {pluginsList.map((plugin, idx) => (
                  <div key={plugin.name + idx} className="plugin-chip">
                    <span className="plugin-name">{plugin.name}</span>
                    {plugin.version && (
                      <span className="plugin-version">{plugin.version}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* TAB: CVE AUDIT */}
          <div className={`tab-pane ${activeTab === 'cve' ? 'active' : ''}`}>
            <div className="flex items-center justify-between gap-3 mb-3 text-left">
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {['all', 'critical', 'high', 'medium', 'low'].map(lvl => (
                  <button
                    key={lvl}
                    type="button"
                    className={`text-[11px] font-mono font-semibold px-2.5 py-1 rounded-full border transition-all ${
                      cveFilter === lvl
                        ? 'bg-teal-500/20 text-teal-300 border-teal-500/50'
                        : 'bg-white/5 text-white/50 border-white/10 hover:text-white'
                    }`}
                    onClick={() => setCveFilter(lvl)}
                  >
                    {lvl.toUpperCase()}
                  </button>
                ))}
              </div>
              <span className="font-mono text-xs text-white/40 whitespace-nowrap">
                {isEn ? 'Entries: ' : 'Записей: '}<b>{filteredCves.length}</b> / {cveList.length}
              </span>
            </div>

            {filteredCves.length === 0 ? (
              <div className="text-center py-12 font-mono text-xs text-white/40">
                {isEn ? 'No CVE entries match the selected filter.' : 'Нет записей CVE, соответствующих фильтру.'}
              </div>
            ) : (
              <div className="cve-audit-wrap">
                {filteredCves.map((cve, idx) => {
                  const sev = (cve.severity || 'low').toLowerCase();
                  return (
                    <div key={cve.cveId || idx} className={`cve-item-card ${sev}`}>
                      <div className="cve-header-row">
                        <div className="cve-title-wrap">
                          <span className={`cve-badge ${sev}`}>{cve.severity || 'INFO'}</span>
                          <span className="cve-id">{cve.cveId}</span>
                          <span className="cve-name">{cve.title}</span>
                        </div>
                        {cve.affectedVersions && (
                          <span className="text-[10px] font-mono text-white/40 bg-white/5 px-2 py-0.5 rounded border border-white/5">
                            {cve.affectedVersions}
                          </span>
                        )}
                      </div>
                      <p className="cve-desc">{cve.description}</p>
                      {cve.mitigation && (
                        <div className="cve-meta-footer">
                          <span>{isEn ? 'Mitigation:' : 'Устранение:'} <span className="cve-mitigation">{cve.mitigation}</span></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* TAB: NETWORK & OSINT */}
          <div className={`tab-pane ${activeTab === 'network' ? 'active' : ''}`}>
            <div className="network-osint-grid">
              {/* Card 1: Datacenter & Geolocation */}
              <div className="network-card">
                <div className="network-card-header">
                  <div className="network-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    <span>{isEn ? 'Datacenter & Geo' : 'Геолокация и Дата-центр'}</span>
                  </div>
                  <span className="text-[10px] font-mono text-white/40">{geoData?.countryCode || 'LOC'}</span>
                </div>
                <div className="network-kv-list">
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'Country & City' : 'Страна и город'}:</span>
                    <span className="network-kv-val">{geoData ? `${geoData.country || 'N/A'}, ${geoData.city || 'N/A'}` : (isEn ? 'Not queried' : 'Не опрашивалось')}</span>
                  </div>
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'ISP / Provider' : 'Провайдер'}:</span>
                    <span className="network-kv-val">{geoData?.isp || geoData?.org || 'N/A'}</span>
                  </div>
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'Autonomous System' : 'ASN Маршрут'}:</span>
                    <span className="network-kv-val text-teal-400">{geoData?.as || 'N/A'}</span>
                  </div>
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'Resolved IP' : 'Резолв IP'}:</span>
                    <span className="network-kv-val select-text">{displayData.ip || targetHost || '127.0.0.1'}</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Anti-DDoS & WAF Protection */}
              <div className="network-card">
                <div className="network-card-header">
                  <div className="network-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    <span>{isEn ? 'Anti-DDoS & Protection' : 'Защита и Стены'}</span>
                  </div>
                  <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${wafData?.detected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/40'}`}>
                    {wafData?.detected ? (isEn ? 'SHIELD ACTIVE' : 'ЗАЩИТА АКТИВНА') : (isEn ? 'DIRECT NODE' : 'ПРЯМОЙ УЗЕЛ')}
                  </span>
                </div>
                <div className="network-kv-list">
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'Shield Type' : 'Провайдер защиты'}:</span>
                    <span className="network-kv-val text-white font-semibold">{wafData?.provider || (isEn ? 'Direct Host' : 'Прямой узел')}</span>
                  </div>
                  <div className="network-kv-row">
                    <span className="network-kv-key">{isEn ? 'Detection Method' : 'Метод детекта'}:</span>
                    <span className="network-kv-val text-white/70">{wafData?.detected ? (isEn ? 'ASN & Routing Fingerprint' : 'ASN & Сигнатурный отпечаток') : (isEn ? 'Standard Gateway' : 'Стандартный шлюз')}</span>
                  </div>
                  <div className="text-[11px] text-white/50 leading-relaxed pt-1 border-t border-white/5">
                    {wafData?.details || (isEn ? 'No known Anti-DDoS scrubbing network detected in front of backend socket.' : 'Сторонняя фильтрующая Anti-DDoS сеть перед сокетом не обнаружена.')}
                  </div>
                </div>
              </div>

              {/* Card 3: Audited Service Ports */}
              <div className="network-card">
                <div className="network-card-header">
                  <div className="network-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
                    <span>{isEn ? 'Service Ports Recon' : 'Аудит сервисных портов'}</span>
                  </div>
                  <span className="text-[10px] font-mono text-white/40">
                    {portsList.length > 0 ? `${portsList.filter(p => (p.status||'').toUpperCase() === 'OPEN').length} OPEN` : (isEn ? 'Standby' : 'Готово')}
                  </span>
                </div>
                {portsList.length === 0 ? (
                  <div className="text-center py-6 text-xs text-white/40 font-mono">
                    {isEn ? 'Port scan was not enabled during extraction' : 'Сканирование портов не было выбрано при извлечении'}
                  </div>
                ) : (
                  <table className="ports-table">
                    <thead>
                      <tr>
                        <th>{isEn ? 'Port' : 'Порт'}</th>
                        <th>{isEn ? 'Service' : 'Сервис'}</th>
                        <th style={{ textAlign: 'right' }}>{isEn ? 'State' : 'Статус'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portsList.map((p, idx) => {
                        const st = (p.status || 'closed').toLowerCase();
                        return (
                          <tr key={p.port || idx}>
                            <td className="font-bold text-white">{p.port}</td>
                            <td className="text-white/70">{p.service}</td>
                            <td style={{ textAlign: 'right' }}>
                              <span className={`port-status-badge ${st}`}>{p.status}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Card 4: Discovered Subdomains & Nodes */}
              <div className="network-card">
                <div className="network-card-header">
                  <div className="network-card-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                    <span>{isEn ? 'Subdomains & Hidden Nodes' : 'Поддомены и скрытые узлы'}</span>
                  </div>
                  <span className="text-[10px] font-mono text-teal-400">{subdomainsList.length}</span>
                </div>
                {subdomainsList.length === 0 ? (
                  <div className="text-center py-6 text-xs text-white/40 font-mono">
                    {isEn ? 'No active subdomains resolved for host' : 'Активные поддомены не обнаружены или хост является IP'}
                  </div>
                ) : (
                  <div className="subdomains-grid max-h-[160px] overflow-y-auto">
                    {subdomainsList.map((sub, idx) => (
                      <div key={sub.subdomain || idx} className="subdomain-badge select-text">
                        <span className="dot" />
                        <span className="text-white font-medium">{sub.subdomain}</span>
                        <span className="text-white/40">({sub.ip})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Card 5: DNS Records */}
              {dnsList.length > 0 && (
                <div className="network-card col-span-full">
                  <div className="network-card-header">
                    <div className="network-card-title">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                      <span>{isEn ? 'DNS Records (A / SRV / CNAME)' : 'DNS-записи (A / SRV / CNAME)'}</span>
                    </div>
                  </div>
                  <div className="subdomains-grid">
                    {dnsList.map((rec, idx) => (
                      <div key={idx} className="subdomain-badge select-text">
                        <span className="font-bold text-teal-400">{rec.type}</span>
                        <span className="text-white">{rec.host}</span>
                        <span className="text-white/40">&rarr; {rec.value}</span>
                        {rec.ttl && <span className="text-[10px] text-white/30">TTL: {rec.ttl}s</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TAB: MODS MANIFEST */}
          <div className={`tab-pane ${activeTab === 'mods' ? 'active' : ''}`}>
            <div className="players-search-line">
              <div className="relative flex-1">
                <input
                  type="text"
                  className="w-full bg-[#0c0e14] border border-white/10 rounded-xl px-4 py-2 text-xs font-mono text-white placeholder-white/40 focus:outline-none focus:border-teal-500/50"
                  placeholder={isEn ? 'Filter Forge/Fabric mods...' : 'Фильтровать моды Forge/Fabric...'}
                  value={modFilter}
                  onChange={(e) => setModFilter(e.target.value)}
                />
              </div>
              <span className="font-mono text-xs text-white/50 whitespace-nowrap">
                {isEn ? 'Showing: ' : 'Показано: '}<b>{filteredMods.length}</b> / {modsList.length}
              </span>
            </div>

            {filteredMods.length === 0 ? (
              <div className="text-center py-12 font-mono text-xs text-white/40">
                {isEn ? 'No Forge/Fabric mods discovered (server may be Vanilla/Paper or withhold FML list)' : 'Моды Forge/Fabric не обнаружены (сервер может быть Vanilla/Paper или скрывать FML список)'}
              </div>
            ) : (
              <div className="plugins-chips-grid max-h-[460px] overflow-y-auto">
                {filteredMods.map((mod, idx) => (
                  <div key={mod.name + idx} className="plugin-chip">
                    <span className="plugin-name">{mod.name}</span>
                    {mod.version && (
                      <span className="plugin-version">{mod.version}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* TAB 5: CODE WITH FORMAT SWITCHER */}
          <div className={`tab-pane ${activeTab === 'code' ? 'active' : ''}`}>
            <div className="raw-code-container text-left">

              {/* Code Format Switcher Toolbar */}
              <div className="code-toolbar-bar">
                <div className="code-format-pills">
                  {['json', 'html', 'yaml', 'txt'].map(fmt => (
                    <button
                      key={fmt}
                      type="button"
                      className={`code-format-pill ${outputFormat === fmt ? 'active' : ''}`}
                      onClick={() => onFormatChange && onFormatChange(fmt)}
                      title={isEn ? `Switch to .${fmt}` : `Переключить на .${fmt}`}
                    >
                      .{fmt}
                    </button>
                  ))}
                </div>

                <div className="code-meta-info">
                  <span className="text-white font-medium select-text">
                    stormblack-{(displayData.hostname || displayData.ip || targetHost || 'dump').replace(/[:.]/g, '_')}.{outputFormat}
                  </span>
                  <span>&bull;</span>
                  <span>{codeLinesCount} {isEn ? 'lines' : 'строк'}</span>
                  <span>&bull;</span>
                  <span className="text-teal-400 font-bold">{codeSizeFormatted}</span>
                </div>

                <div className="code-quick-actions">
                  <button
                    type="button"
                    className="tool-icon-btn"
                    onClick={() => onCopy && onCopy(formattedCode)}
                    title={isEn ? 'Copy code' : 'Скопировать код'}
                  >
                    <svg viewBox="0 0 24 24">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                    <span>{isEn ? 'Copy' : 'Копировать'}</span>
                  </button>

                  <button
                    type="button"
                    className="tool-icon-btn primary-download"
                    onClick={() => onDownload && onDownload(formattedCode, outputFormat)}
                    title={isEn ? `Download .${outputFormat}` : `Скачать .${outputFormat}`}
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>.{outputFormat.toUpperCase()}</span>
                  </button>
                </div>
              </div>

              {/* Code Scroll Pane with Syntax Highlighting */}
              <div className="code-scroll-pane">
                {outputFormat === 'json' && (
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: renderHighlightedJson(formattedCode)
                    }}
                  />
                )}
                {outputFormat === 'yaml' && (
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: renderHighlightedYaml(formattedCode)
                    }}
                  />
                )}
                {outputFormat === 'html' && (
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: renderHighlightedHtml(formattedCode)
                    }}
                  />
                )}
                {outputFormat === 'txt' && (
                  <pre>{formattedCode}</pre>
                )}
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default ExtractionResults;